import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSocialLinks } from '../hooks/useSocialLinks';
import './Navigation.css';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Use centralized social links
  const { socialLinks, loading: socialLoading } = useSocialLinks('default');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/episodes', label: 'Episodes' },
    { path: '/hosts', label: 'Hosts' },
    { path: '/guests', label: 'Guests' },
    { path: '/membership', label: 'Membership' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`network-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-wrapper">
        <Link to="/" className="network-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_tee-time-13/artifacts/pxvmf042_Golf%20Nuts%20Wide%205%20transp%20WHT.png" 
            alt="Golf Nuts" 
            className="logo-img"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="network-nav desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`network-nav-link ${
                location.pathname === link.path ? 'active' : ''
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Member Clubhouse Links */}
          <div className="nav-divider"></div>
          <Link
            to="/clubhouse/login"
            className="network-nav-link member-login"
          >
            Member Login
          </Link>
          <Link
            to="/clubhouse/register"
            className="btn-nav-cta"
          >
            Join
          </Link>
          
          {/* Social Icons - Using Global Social Links */}
          {!socialLoading && (
            <div className="nav-social-icons">
              {socialLinks.slice(0, 5).map((link, index) => {
                const Icon = link.icon;
                return (
                  <a 
                    key={`nav-${link.name || link.platform || 'social'}-${link.url || index}`}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="nav-social-link"
                    aria-label={link.name}
                    title={link.name}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          )}
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <nav className="mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`mobile-nav-link ${
                location.pathname === link.path ? 'active' : ''
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Member Clubhouse Links */}
          <div className="mobile-nav-divider"></div>
          <Link
            to="/clubhouse/login"
            className="mobile-nav-link member-login"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Member Login
          </Link>
          <Link
            to="/clubhouse/register"
            className="mobile-nav-link member-signup"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Join
          </Link>
          
          {/* Mobile Social Icons - Using Global Social Links */}
          {!socialLoading && (
            <div className="mobile-social-icons">
              {socialLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <a 
                    key={`mobile-${link.name || link.platform || 'social'}-${link.url || index}`}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mobile-social-link"
                    aria-label={link.name}
                  >
                    <Icon size={24} />
                    <span>{link.name}</span>
                  </a>
                );
              })}
            </div>
          )}
        </nav>
      )}
    </header>
  );
};
